import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ApiResponse } from "@app/shared/models/ApiResponse";
import { Http, HttpOptions } from "@capacitor-community/http";
import { environment } from "@env";
import { TranslateService } from "@ngx-translate/core";
import { throwError } from "rxjs";
import { catchError, finalize, retry, tap, timeout } from "rxjs/operators";
import { LoadingBarService } from "../services/loading-bar/loading-bar.service";
import { SharedService } from "../services/shared/shared.service";
import { ToastService } from "../services/toast/toast.service";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  baseUrl = environment.apiBaseUrl;
  apiKey = environment.apiKey;
  subdomain: string;
  tier: string;
  private timeOut = 15000;
  private retry = 0;
  private enableLog = !environment.production;
  private header: HttpHeaders;

  constructor(
    private httpClient: HttpClient,
    private loadingBarSvc: LoadingBarService,
    private sharedSvc: SharedService,
    public translateService: TranslateService,
    private toastSvc: ToastService
  ) {}

  async get(
    endpoint: string,
    customHeader?: HttpHeaders
  ): Promise<ApiResponse> {
    if (this.sharedSvc.getCustomerDetails) {
      this.subdomain =
        this.sharedSvc.getCustomerDetails.customerData.DomainName;
      this.tier = this.sharedSvc.getCustomerDetails.customerData.Tier;
    }

    this.header =
      customHeader ||
      new HttpHeaders({
        tier: this.tier,
        apiKey: this.apiKey,
        subdomain: this.subdomain,
      });

    try {
      const url = `${this.baseUrl}${endpoint}`;
      const http = this.httpClient.get(url, { headers: this.header }).pipe(
        retry(this.retry),
        tap((data) => this.log(data)),
        catchError((e) =>
          throwError(
            e.error || this.translateService.instant("BACKEND_SERVER_ERROR")
          )
        ),
        finalize(() => {})
      );
      const response: any = await http.toPromise();
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async post(endpoint: string, body: any): Promise<ApiResponse> {
    try {
      if (this.sharedSvc.getCustomerDetails) {
        this.subdomain =
          this.sharedSvc.getCustomerDetails.customerData.DomainName;
        this.tier = this.sharedSvc.getCustomerDetails.customerData.Tier;
      }
      this.header = new HttpHeaders({
        tier: this.tier,
        apiKey: this.apiKey,
        subdomain: this.subdomain,
      });
      const url = `${this.baseUrl}${endpoint}`;
      // const urlWithParams = `${this.addParameterToURL(
      //   url,
      //   'action=' + action
      // )}`;
      this.log(url);
      this.loadingBarSvc.set(true);
      const http = this.httpClient
        .post(url, body, { headers: this.header })
        .pipe(
          retry(this.retry),
          tap((data) => this.log(data)),
          catchError((e) =>
            throwError(
              e.error || this.translateService.instant("BACKEND_SERVER_ERROR")
            )
          ),
          finalize(() => {
            this.loadingBarSvc.set(false);
          })
        );
      const response: any = await http.toPromise();
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async delete(url: string, options?: any): Promise<ApiResponse> {
    try {
      const http = this.httpClient.delete(url, options).pipe(
        retry(this.retry),
        timeout(this.timeOut),
        tap((data) => this.log(data)),
        catchError((e) =>
          throwError(
            e.error || this.translateService.instant("BACKEND_SERVER_ERROR")
          )
        ),
        finalize(() => {})
      );
      const response: any = await http.toPromise();
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  async put(
    endpoint: string,
    body: any,
    isFullUrl: boolean = false
  ): Promise<ApiResponse> {
    try {
      const url = isFullUrl ? endpoint : `${this.baseUrl}${endpoint}`;
      // const options = isFullUrl ? {} : { headers: this.header };
      // const http = this.httpClient.put(url, body, options).pipe(
      //   retry(this.retry),
      //   timeout(this.timeOut),
      //   tap((data) => this.log(data)),
      //   catchError((e) => throwError(e.error || 'Backend server error')),
      //   finalize(() => {})
      // );
      // const response: any = await http.toPromise();
      // return response;

      const httpOptions: HttpOptions = {
        url,
        headers: {
          // tier: this.tier,
          apiKey: this.apiKey,
          // subdomain: this.subdomain,
          "Content-Type": "application/json",
        },
        data: body,
        method: "PUT",
      };
      const res: any = await Http.request(httpOptions);
      return res.data;
    } catch (error) {
      console.error(error);
    }
  }

  async getUrlQueryParameters(queries: object, url?: string) {
    try {
      let queryString = "";
      if (queries) {
        let i = 0;
        for (const [key, value] of Object.entries(queries)) {
          if (!url) {
            queryString = `${queryString}${i === 0 ? "?" : "&"}`;
            queryString = `${queryString}${key}=${value}`;
          } else {
            if (i === 0) {
              // queryString = `${url}${url.split('?')[1] ? '&' : '?'}`;
              queryString = `${url.split("?")[1] ? "&" : "?"}`;
            } else {
              queryString = `${queryString}${i === 0 ? "?" : "&"}`;
            }
            queryString = `${queryString}${key}=${value}`;
          }
          i++;
        }
      }
      return queryString;
    } catch (error) {
      console.error(error);
    }
  }

  addParameterToURL(url: string, param: string): string {
    url = url + (url.split("?")[1] ? "&" : "?") + param;
    return url;
  }

  private log(data: any) {
    if (this.enableLog) {
      console.log(data);
    }
  }

  async postProvider(endpoint: string, body: any, isPublicURL?): Promise<ApiResponse> {
    try {
      let baseURL = this.baseUrl
      if(isPublicURL){
        baseURL = baseURL.replace("/public/", "");
      }
      if (this.sharedSvc.getCustomerDetails) {
        this.subdomain =
          this.sharedSvc.getCustomerDetails.customerData.DomainName;
        this.tier = this.sharedSvc.getCustomerDetails.customerData.Tier;
      }
      this.header = new HttpHeaders({
        tier: this.tier,
        apiKey: this.apiKey,
        subdomain: this.subdomain,
      });
      const url = `${baseURL}${endpoint}`;
      this.log(url);
      this.loadingBarSvc.set(true);
      const http = this.httpClient
        .post(url, body, { headers: this.header })
        .pipe(
          retry(this.retry),
          tap((data) => this.log(data)),
          catchError((e) =>
            throwError(
              e.error || this.translateService.instant("BACKEND_SERVER_ERROR")
            )
          ),
          finalize(() => {
            this.loadingBarSvc.set(false);
          })
        );
      const response: any = await http.toPromise();
      return response;
    } catch (error) {
      console.error(error);
      this.errorHandler(error);
    }
  }

  async putProvider(
    endpoint: string,
    body: any,
    isFullUrl: boolean = false
  ): Promise<ApiResponse> {

    try {
      this.header = new HttpHeaders({
        apiKey: this.apiKey,
      });
      const url = `${this.baseUrl}${endpoint}`;
      const http = this.httpClient
        .put(url, body, { headers: this.header })
        .pipe(
          catchError((e) =>
            throwError(
              e.error || this.translateService.instant("BACKEND_SERVER_ERROR")
            )
          ));
      const response: any = await http.toPromise();
      return response;
    } catch (error) {
      console.error(error);
      this.errorHandler(error);
    }
  }

  async errorHandler(error){
    await this.toastSvc.presentErrorToast(error?.error?.message || error);
  }
}
